/**
 * AI Recruit - Forgot Password Page
 * Request password reset via email
 */

import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Bot, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react'
import { authApi } from '@/services/api'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import toast from 'react-hot-toast'

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email) {
      toast.error('Please enter your email address')
      return
    }

    setIsLoading(true)

    try {
      const response = await authApi.requestPasswordReset(email)
      
      // In development mode (when email is not configured), show the reset URL
      if (response.data?.dev_reset_url) {
        console.log('🔗 Password Reset URL:', response.data.dev_reset_url)
        console.log('🔑 Reset Token:', response.data.dev_token)
        
        // Show a more helpful toast with a link
        toast.success(
          (t) => (
            <div>
              <p className="font-medium mb-2">Password reset link generated!</p>
              <a 
                href={response.data.dev_reset_url}
                className="text-blue-600 hover:text-blue-800 underline text-sm"
                onClick={() => toast.dismiss(t.id)}
              >
                Click here to reset password
              </a>
              <p className="text-xs text-gray-500 mt-1">(Email not configured - using direct link)</p>
            </div>
          ),
          { duration: 10000 }
        )
      } else {
        toast.success('Password reset email sent! Check your inbox.')
      }
      
      setEmailSent(true)
    } catch (error: any) {
      console.error('Password reset request error:', error)
      // Even on error, show success message for security (don't reveal if email exists)
      setEmailSent(true)
    } finally {
      setIsLoading(false)
    }
  }

  if (emailSent) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - Success Message */}
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            {/* Logo and title */}
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center mb-6">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Check Your Email
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                If an account exists with <span className="font-medium">{email}</span>, you will receive password reset instructions.
              </p>
            </div>

            <div className="mt-8">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> The reset link will expire in 1 hour. If you don't receive an email, please check your spam folder.
                </p>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => navigate('/auth/login')}
                  className="btn-primary w-full py-3"
                >
                  Back to Login
                </button>

                <button
                  onClick={() => {
                    setEmailSent(false)
                    setEmail('')
                  }}
                  className="w-full py-3 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Try another email
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Background */}
        <div className="hidden lg:block relative w-0 flex-1">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600">
            <div className="absolute inset-0 bg-black opacity-20"></div>
            <div className="relative h-full flex items-center justify-center p-12">
              <div className="max-w-lg text-center text-white">
                <Bot className="w-24 h-24 mx-auto mb-8 opacity-90" />
                <h1 className="text-4xl font-bold mb-6">
                  Secure Account Recovery
                </h1>
                <p className="text-xl text-gray-200 opacity-90 leading-relaxed">
                  We'll send you secure instructions to reset your password and regain access to your account.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Forgot Password Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {/* Logo and title */}
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center mb-6">
              <Bot className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">
              Forgot Password?
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              No worries! Enter your email and we'll send you reset instructions.
            </p>
          </div>

          {/* Forgot password form */}
          <div className="mt-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Email field */}
              <div>
                <label htmlFor="email" className="form-label">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input pl-10"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              {/* Submit button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full py-3 relative"
                >
                  {isLoading ? (
                    <LoadingSpinner size="sm" variant="white" />
                  ) : (
                    'Send Reset Instructions'
                  )}
                </button>
              </div>
            </form>

            {/* Back to login link */}
            <div className="mt-6">
              <Link
                to="/auth/login"
                className="flex items-center justify-center space-x-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Login</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Background */}
      <div className="hidden lg:block relative w-0 flex-1">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="relative h-full flex items-center justify-center p-12">
            <div className="max-w-lg text-center text-white">
              <Bot className="w-24 h-24 mx-auto mb-8 opacity-90" />
              <h1 className="text-4xl font-bold mb-6">
                Secure Account Recovery
              </h1>
              <p className="text-xl text-gray-200 opacity-90 leading-relaxed">
                We'll send you secure instructions to reset your password and regain access to your account.
              </p>
              <div className="mt-8 space-y-4 text-left">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>Secure token-based reset</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>1-hour expiration for security</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>Email delivery confirmation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
